// Service de chargement régional des points kilométriques
import maplibregl, { type LngLatBounds } from 'maplibre-gl';

export interface PKPoint {
  id: string;
  code_ligne: string;
  pk: number;
  vitesse: number | null;
  altitude: number;
  lat: number;
  lon: number;
}

export interface RegionalPKData {
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  points: PKPoint[];
  loadedAt: number;
}

export class PKRegionalLoader {
  private cache = new Map<string, RegionalPKData>();
  private allPKData: PKPoint[] = [];
  private isFullyLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  // Taille des tuiles régionales (en degrés)
  private readonly TILE_SIZE = 0.1; // ~11km à la latitude 50°
  private readonly CACHE_TTL = 300000; // 5 minutes

  // Charger toutes les données PK une seule fois au démarrage
  private async loadAllPKData(): Promise<void> {
    if (this.isFullyLoaded) return;
    
    if (this.loadingPromise) {
      return this.loadingPromise;
    }
    
    this.loadingPromise = this.fetchAllPKData();
    return this.loadingPromise;
  }

  private async fetchAllPKData(): Promise<void> {
    try {
      console.log('📡 Chargement complet des PK (366K points)...');
      const startTime = performance.now();
      
      const response = await fetch('/pks.csv');
      const csvText = await response.text();
      
      this.allPKData = this.parseCSVComplete(csvText);
      this.isFullyLoaded = true;
      
      const endTime = performance.now();
      console.log(`✅ ${this.allPKData.length} PK chargés en ${Math.round(endTime - startTime)}ms`);
      
    } catch (error) {
      console.error('❌ Erreur chargement PK complet:', error);
      throw error;
    }
  }

  private parseCSVComplete(csvText: string): PKPoint[] {
    const lines = csvText.split('\n');
    const results: PKPoint[] = [];
    
    for (let i = 1; i < lines.length; i++) { // Skip header
      const line = lines[i];
      if (!line.trim()) continue;
      
      const values = line.split(',').map(v => v.replace(/"/g, ''));
      if (values.length >= 8) {
        const lat = parseFloat(values[6]);
        const lon = parseFloat(values[7]);
        
        if (!isNaN(lat) && !isNaN(lon)) {
          results.push({
            id: `pk-${values[0]}-${values[1]}`,
            code_ligne: values[0],
            pk: parseFloat(values[1]),
            vitesse: values[2] === 'NULL' ? null : parseFloat(values[2]),
            altitude: parseFloat(values[3]),
            lat: lat,
            lon: lon
          });
        }
      }
    }
    
    return results;
  }

  // Générer une clé de tuile pour une coordonnée (utilisé pour les extensions futures)
  // private getTileKey(lat: number, lon: number): string {
  //   const tileY = Math.floor(lat / this.TILE_SIZE);
  //   const tileX = Math.floor(lon / this.TILE_SIZE);
  //   return `${tileX}_${tileY}`;
  // }

  // Obtenir toutes les tuiles qui intersectent avec les bounds
  private getTileKeys(bounds: LngLatBounds): string[] {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    
    const minTileX = Math.floor(sw.lng / this.TILE_SIZE);
    const maxTileX = Math.floor(ne.lng / this.TILE_SIZE);
    const minTileY = Math.floor(sw.lat / this.TILE_SIZE);
    const maxTileY = Math.floor(ne.lat / this.TILE_SIZE);
    
    const tileKeys: string[] = [];
    for (let x = minTileX; x <= maxTileX; x++) {
      for (let y = minTileY; y <= maxTileY; y++) {
        tileKeys.push(`${x}_${y}`);
      }
    }
    
    return tileKeys;
  }

  // Filtrer les PK dans une zone géographique
  private filterPKInBounds(points: PKPoint[], bounds: LngLatBounds, zoom: number): PKPoint[] {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    
    // Ajuster la densité selon le zoom
    let skipFactor = 1;
    if (zoom < 12) skipFactor = 10;      // Zoom faible: 1 PK sur 10
    else if (zoom < 14) skipFactor = 5;  // Zoom moyen: 1 PK sur 5  
    else if (zoom < 16) skipFactor = 2;  // Zoom élevé: 1 PK sur 2
    // zoom >= 16: tous les PK
    
    const filtered: PKPoint[] = [];
    let counter = 0;
    
    for (const point of points) {
      if (point.lat >= sw.lat && point.lat <= ne.lat && 
          point.lon >= sw.lng && point.lon <= ne.lng) {
        
        if (counter % skipFactor === 0) {
          filtered.push(point);
        }
        counter++;
      }
    }
    
    return filtered;
  }

  // API principale: charger les PK pour une zone
  async loadPKForRegion(bounds: LngLatBounds, zoom: number): Promise<PKPoint[]> {
    await this.loadAllPKData();
    
    const tileKeys = this.getTileKeys(bounds);
    const now = Date.now();
    const allRegionalPoints: PKPoint[] = [];
    
    // Vérifier le cache pour chaque tuile
    for (const tileKey of tileKeys) {
      const cached = this.cache.get(tileKey);
      
      if (cached && (now - cached.loadedAt) < this.CACHE_TTL) {
        // Utiliser le cache
        allRegionalPoints.push(...cached.points);
      } else {
        // Créer une nouvelle entrée de cache pour cette tuile
        const tileBounds = this.getTileBounds(tileKey);
        const tileMapBounds = new maplibregl.LngLatBounds(
          [tileBounds.west, tileBounds.south],
          [tileBounds.east, tileBounds.north]
        );
        
        const tilePoints = this.filterPKInBounds(this.allPKData, tileMapBounds, 18); // Zoom max pour le cache
        
        this.cache.set(tileKey, {
          bounds: tileBounds,
          points: tilePoints,
          loadedAt: now
        });
        
        allRegionalPoints.push(...tilePoints);
      }
    }
    
    // Filtrer selon le zoom demandé et les bounds exacts
    const finalPoints = this.filterPKInBounds(allRegionalPoints, bounds, zoom);
    
    console.log(`🛤️ Chargés ${finalPoints.length} PK pour zoom ${zoom} (${tileKeys.length} tuiles)`);
    return finalPoints;
  }

  private getTileBounds(tileKey: string) {
    const [x, y] = tileKey.split('_').map(Number);
    return {
      west: x * this.TILE_SIZE,
      east: (x + 1) * this.TILE_SIZE,
      south: y * this.TILE_SIZE,
      north: (y + 1) * this.TILE_SIZE
    };
  }

  // Nettoyer le cache ancien
  cleanOldCache(): void {
    const now = Date.now();
    for (const [key, data] of this.cache.entries()) {
      if (now - data.loadedAt > this.CACHE_TTL) {
        this.cache.delete(key);
      }
    }
  }

  // Obtenir les statistiques du cache
  getCacheStats() {
    return {
      totalTiles: this.cache.size,
      totalPKLoaded: this.allPKData.length,
      isFullyLoaded: this.isFullyLoaded
    };
  }
}

export const pkLoader = new PKRegionalLoader();